import {
  MAX_CRITERIA_TO_SHOW,
  MAX_PRESCREENER_VERIFIER_QUESTIONS,
  MAX_QUESTIONS_PER_APPLY_FLOW,
} from '@lib/questionnaire/trialApplication/eligibilityCriteria/consts'
import kebabCase from 'lodash/kebabCase'
import type { CategoryType, StageType } from './TrialApplyFlowStepTypes'
import {
  StageSegmentMapping,
  SurveyCategorySegmentMapping,
  TrialApplyFlowSteps,
} from './TrialApplyFlowSteps'

export const getProgressBarProps = (
  pathParts: string[],
  questionCount?: number,
) => {
  if (pathParts[pathParts.length - 1] === 'start') {
    return {
      currentStepIndex: 0,
      hideBackNavigation: false,
      numberOfSteps: 1,
      segment: 1,
    }
  }

  const segment = getSegment(pathParts)
  const numberOfSteps = getNumberOfSteps(pathParts)
  const currentStepIndex = getCurrentStepIndex(pathParts, questionCount)

  return {
    currentStepIndex,
    hideBackNavigation: false,
    numberOfSteps,
    segment,
  }
}

export const getStageFromPath = (pathParts: string[]): StageType => {
  if (pathParts.includes('apply-survey')) {
    return 'applySurvey'
  } else if (pathParts.includes('apply-recommendations')) {
    return 'applyRecommendations'
  } else if (pathParts.includes('apply-success')) {
    return 'applySuccess'
  }
  return 'trialApply'
}

const getSurveyCategoryFromPath = (pathParts: string[]): CategoryType => {
  if (pathParts.slice(0, -1).includes('conditions')) {
    return 'conditions'
  } else if (pathParts.includes('prescreener')) {
    return 'prescreener'
  } else if (pathParts.includes('postscreener')) {
    return 'postscreener'
  } else if (pathParts.includes('locations')) {
    return 'locations'
  }
  return 'demographics'
}
const getSegment = (pathParts: string[]): number => {
  const stage = getStageFromPath(pathParts)

  if (stage === 'applySurvey') {
    return getSegmentForSurveyCategory(pathParts)
  }
  return StageSegmentMapping[stage]
}

const getSegmentForSurveyCategory = (pathParts: string[]) => {
  const category = getSurveyCategoryFromPath(pathParts)
  return SurveyCategorySegmentMapping[category]
}

const getNumberOfSteps = (pathParts: string[]) => {
  const stage = getStageFromPath(pathParts)

  if (stage === 'applySurvey') {
    return getNumberOfStepsForSurveyCategory(pathParts)
  } else if (stage === 'applyRecommendations') {
    return MAX_QUESTIONS_PER_APPLY_FLOW
  }
  return TrialApplyFlowSteps[stage].length
}

const getNumberOfStepsForSurveyCategory = (pathParts: string[]) => {
  const category = getSurveyCategoryFromPath(pathParts)

  if (category === 'prescreener' || category === 'postscreener') {
    return MAX_QUESTIONS_PER_APPLY_FLOW // Lots of questions in the prescreener and postscreener (and other apply-success screens)
  }
  return TrialApplyFlowSteps.applySurvey.length // demographics + locations + conditions
}

const getCurrentStepIndex = (pathParts: string[], questionCount = 0) => {
  const stage = getStageFromPath(pathParts)

  if (stage === 'applySurvey') {
    return getCurrentStepIndexForSurveyCategory(pathParts, questionCount)
  } else if (stage === 'applyRecommendations') {
    return MAX_QUESTIONS_PER_APPLY_FLOW - 1
  }
  const index = TrialApplyFlowSteps[stage].findIndex(
    (step) => step.stepId === pathParts[pathParts.length - 1],
  )
  return index >= 0 ? index : TrialApplyFlowSteps[stage].length
}

const getCurrentStepIndexForSurveyCategory = (
  pathParts: string[],
  questionCount = 0,
) => {
  const category = getSurveyCategoryFromPath(pathParts)

  if (
    category === 'prescreener' &&
    pathParts.includes('social-anxiety-disorder')
  ) {
    const parsedStepIndex = parseInt(pathParts[pathParts.length - 1] ?? '1', 10)
    const stepIndex = isNaN(parsedStepIndex) ? 1 : parsedStepIndex

    return MAX_CRITERIA_TO_SHOW + stepIndex
  } else if (category === 'prescreener') {
    return questionCount
  } else if (category === 'postscreener') {
    return (
      questionCount + MAX_CRITERIA_TO_SHOW + MAX_PRESCREENER_VERIFIER_QUESTIONS
    )
  }
  const index = TrialApplyFlowSteps['applySurvey'].findIndex(
    (step) => kebabCase(step.stepId) === pathParts[pathParts.length - 1],
  )
  return index >= 0 ? index : TrialApplyFlowSteps['applySurvey'].length
}
